import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { useGameStore } from '../store/gameStore';
import { ArrowLeft, Trophy, Target, Zap, Flame, Clock, Medal, Crown, type LucideIcon } from 'lucide-react';

interface PlayerStats {
  totalSessions: number;
  bestAccuracy: number;
  bestReaction: number;
  streak: number;
  totalEnergy: number;
}

interface RivalData {
  name: string;
  totalSessions: number;
  bestAccuracy: number;
  bestReaction: number;
  streak: number;
  totalEnergy: number;
}

interface BoardEntry {
  name: string;
  isPlayer: boolean;
  value: number;
}

type LeaderboardType = 'sessions' | 'accuracy' | 'reaction' | 'streak' | 'energy';

type BoardTab = {
  id: LeaderboardType;
  label: string;
  unit: string;
  Icon: LucideIcon;
};

const BOARD_TABS: BoardTab[] = [
  { id: 'sessions', label: '训练次数', unit: '次', Icon: Target },
  { id: 'accuracy', label: '最高准确率', unit: '%', Icon: Trophy },
  { id: 'reaction', label: '最快反应', unit: 'ms', Icon: Clock },
  { id: 'streak', label: '连续天数', unit: '天', Icon: Flame },
  { id: 'energy', label: '总能量', unit: '', Icon: Zap },
];

// Simulated rival data - seeded from player name for consistency
function generateRivals(playerName: string, playerStats: PlayerStats) {
  const names = [
    '清风', '晨曦', '星河', '远望', '聚焦',
    '明眸', '锐视', '恒心', '逐光', '视界',
    '凌峰', '澄澈', '知行', '远航', '曜石',
    '北辰', '青云', '飞羽', '知秋', '云岚'
  ];

  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
  const seeded = (base: number, salt: number) => {
    const n = Math.sin(base * 12.9898 + salt * 78.233) * 43758.5453;
    return n - Math.floor(n);
  };

  const seed = playerName.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const rivals: RivalData[] = [];

  const sessionBase = Math.max(8, playerStats.totalSessions || 0);
  const accuracyBase = clamp(playerStats.bestAccuracy || 72, 60, 100);
  const reactionBase = clamp(playerStats.bestReaction === 9999 ? 780 : playerStats.bestReaction, 260, 1500);
  const streakBase = Math.max(2, playerStats.streak || 0);
  const energyBase = Math.max(18, playerStats.totalEnergy || 0);

  for (let i = 0; i < 19; i++) {
    const nameIdx = (seed + i * 7) % names.length;

    const sessionPower = seeded(seed + i * 17, 1);
    const accuracyPower = seeded(seed + i * 19, 2);
    const reactionPower = seeded(seed + i * 23, 3);
    const streakPower = seeded(seed + i * 29, 4);
    const energyPower = seeded(seed + i * 31, 5);

    rivals.push({
      name: `${names[nameIdx]}${i > 9 ? i : ''}`,
      totalSessions: clamp(Math.round(sessionBase * (0.55 + sessionPower * 1.45) + i), 5, 420),
      bestAccuracy: clamp(Math.round(accuracyBase * (0.85 + accuracyPower * 0.2) + (accuracyPower - 0.5) * 8), 55, 100),
      bestReaction: clamp(Math.round(reactionBase * (0.7 + reactionPower * 0.65) + (reactionPower - 0.5) * 120), 220, 2400),
      streak: clamp(Math.round(streakBase * (0.45 + streakPower * 1.35) + (streakPower - 0.5) * 3), 1, 365),
      totalEnergy: clamp(Math.round(energyBase * (0.6 + energyPower * 1.55) + (energyPower - 0.5) * 30), 10, 9999),
    });
  }

  return rivals;
}

function formatValue(value: number, type: LeaderboardType, hasData: boolean) {
  if (!hasData && type === 'reaction') return '--';
  if (type === 'accuracy') return `${value}%`;
  if (type === 'reaction') return `${value}ms`;
  if (type === 'streak') return `${value}天`;
  if (type === 'sessions') return `${value}次`;
  return `${value}`;
}

function getValueByBoard(stats: PlayerStats, board: LeaderboardType) {
  switch (board) {
    case 'sessions':
      return stats.totalSessions;
    case 'accuracy':
      return stats.bestAccuracy;
    case 'reaction':
      return stats.bestReaction;
    case 'streak':
      return stats.streak;
    case 'energy':
      return stats.totalEnergy;
    default:
      return 0;
  }
}

export default function LeaderboardPage() {
  const navigate = useNavigate();
  const { playerName, levelResults, consecutiveDays, totalEnergy, checkinStreak } = useGameStore();
  const [activeBoard, setActiveBoard] = useState<LeaderboardType>('sessions');

  const hasTrainingData = levelResults.length > 0;

  const playerStats: PlayerStats = useMemo(() => ({
    totalSessions: levelResults.length,
    bestAccuracy: levelResults.length > 0 ? Math.round(Math.max(...levelResults.map(r => r.accuracy))) : 0,
    bestReaction: levelResults.some(r => r.reactionTime > 0) ? Math.round(Math.min(...levelResults.filter(r => r.reactionTime > 0).map(r => r.reactionTime))) : 9999,
    streak: Math.max(consecutiveDays, checkinStreak),
    totalEnergy,
  }), [levelResults, consecutiveDays, checkinStreak, totalEnergy]);

  const rivals = useMemo(() => generateRivals(playerName || '用户', playerStats), [playerName, playerStats]);

  const leaderboard = useMemo(() => {
    const player: BoardEntry = {
      name: playerName || '我',
      isPlayer: true,
      value: getValueByBoard(playerStats, activeBoard),
    };

    const entries: BoardEntry[] = rivals.map(r => ({
      name: r.name,
      isPlayer: false,
      value:
        activeBoard === 'sessions' ? r.totalSessions :
        activeBoard === 'accuracy' ? r.bestAccuracy :
        activeBoard === 'reaction' ? r.bestReaction :
        activeBoard === 'streak' ? r.streak :
        r.totalEnergy,
    }));

    const all = [...entries, player];

    if (activeBoard === 'reaction') {
      all.sort((a, b) => a.value - b.value);
    } else {
      all.sort((a, b) => b.value - a.value);
    }

    return all;
  }, [activeBoard, playerStats, rivals, playerName]);

  const playerRank = leaderboard.findIndex(e => e.isPlayer) + 1;
  const tabConfig = BOARD_TABS.find(t => t.id === activeBoard)!;
  const topThree = leaderboard.slice(0, 3);

  const metricValues = leaderboard.map(item => item.value);
  const minValue = Math.min(...metricValues);
  const maxValue = Math.max(...metricValues);

  const getBarWidth = (value: number) => {
    if (maxValue === minValue) return 100;
    if (activeBoard === 'reaction') {
      return ((maxValue - value) / (maxValue - minValue)) * 100;
    }
    return (value / maxValue) * 100;
  };

  const getRankLabel = (rank: number) => {
    if (rank === 1) return 'TOP 1';
    if (rank === 2) return 'TOP 2';
    if (rank === 3) return 'TOP 3';
    return `#${rank}`;
  };

  return (
    <div className="min-h-screen w-full bg-[var(--bg)]">
      <header className="sticky top-0 z-30 bg-white/85 dark:bg-gray-800/85 backdrop-blur-md border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between max-w-3xl mx-auto px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-300">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-semibold text-gray-800 dark:text-gray-100">排行榜</h1>
          <div className="w-9" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-5 pb-28 space-y-4">
        <motion.div
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="rounded-2xl p-5 text-white bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-800 shadow-lg shadow-slate-300/30 dark:shadow-none"
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-indigo-200 text-xs">当前名次</p>
              <p className="text-3xl font-bold mt-1">第 {playerRank} 名</p>
              <p className="text-indigo-200 text-xs mt-1">
                {tabConfig.label}：{formatValue(getValueByBoard(playerStats, activeBoard), activeBoard, hasTrainingData)}
              </p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
              <Trophy className="w-7 h-7 text-indigo-100" />
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {BOARD_TABS.map(tab => {
            const ActiveIcon = tab.Icon;
            const isActive = activeBoard === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveBoard(tab.id)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium transition-all border ${
                  isActive
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-200/50 dark:shadow-none'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600'
                }`}
              >
                <ActiveIcon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {activeBoard === 'sessions' ? (
          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4"
          >
            <div className="flex items-end justify-center gap-3 h-44">
              {topThree[1] && (
                <div className="flex flex-col items-center w-[30%]">
                  <div className={`mb-2 w-11 h-11 rounded-full flex items-center justify-center text-sm font-semibold ${
                    topThree[1].isPlayer
                      ? 'bg-indigo-500 text-white ring-2 ring-indigo-200 dark:ring-indigo-700'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                  }`}>
                    {topThree[1].name.slice(0, 1)}
                  </div>
                  <p className={`text-xs font-medium truncate max-w-full ${topThree[1].isPlayer ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-200'}`}>
                    {topThree[1].name}{topThree[1].isPlayer ? '（我）' : ''}
                  </p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                    {formatValue(topThree[1].value, activeBoard, hasTrainingData)}
                  </p>
                  <div className="mt-2 w-full h-20 rounded-t-xl bg-gradient-to-t from-slate-300 to-slate-200 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center">
                    <span className="text-xs font-semibold text-slate-600 dark:text-gray-200">TOP 2</span>
                  </div>
                </div>
              )}

              {topThree[0] && (
                <div className="flex flex-col items-center w-[34%]">
                  <Crown className="w-5 h-5 text-amber-500 mb-1" />
                  <div className={`mb-2 w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold ${
                    topThree[0].isPlayer
                      ? 'bg-indigo-500 text-white ring-2 ring-indigo-200 dark:ring-indigo-700'
                      : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                  }`}>
                    {topThree[0].name.slice(0, 1)}
                  </div>
                  <p className={`text-sm font-semibold truncate max-w-full ${topThree[0].isPlayer ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-800 dark:text-gray-100'}`}>
                    {topThree[0].name}{topThree[0].isPlayer ? '（我）' : ''}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {formatValue(topThree[0].value, activeBoard, hasTrainingData)}
                  </p>
                  <div className="mt-2 w-full h-28 rounded-t-xl bg-gradient-to-t from-amber-300 to-amber-200 dark:from-amber-700/80 dark:to-amber-600/70 flex items-center justify-center">
                    <span className="text-xs font-semibold text-amber-800 dark:text-amber-100">TOP 1</span>
                  </div>
                </div>
              )}

              {topThree[2] && (
                <div className="flex flex-col items-center w-[28%]">
                  <div className={`mb-2 w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                    topThree[2].isPlayer
                      ? 'bg-indigo-500 text-white ring-2 ring-indigo-200 dark:ring-indigo-700'
                      : 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                  }`}>
                    {topThree[2].name.slice(0, 1)}
                  </div>
                  <p className={`text-xs font-medium truncate max-w-full ${topThree[2].isPlayer ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-200'}`}>
                    {topThree[2].name}{topThree[2].isPlayer ? '（我）' : ''}
                  </p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                    {formatValue(topThree[2].value, activeBoard, hasTrainingData)}
                  </p>
                  <div className="mt-2 w-full h-16 rounded-t-xl bg-gradient-to-t from-orange-300 to-orange-200 dark:from-orange-800/70 dark:to-orange-700/60 flex items-center justify-center">
                    <span className="text-xs font-semibold text-orange-800 dark:text-orange-100">TOP 3</span>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {topThree.map((entry, idx) => {
              const rank = idx + 1;
              return (
                <motion.div
                  key={entry.name}
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: idx * 0.05 }}
                  className={`rounded-xl border p-4 ${
                    entry.isPlayer
                      ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-700'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-[10px] px-2 py-1 rounded-full font-semibold ${
                      rank === 1
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                        : rank === 2
                          ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                          : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                    }`}>{getRankLabel(rank)}</span>
                    {rank === 1 ? <Crown className="w-4 h-4 text-amber-500" /> : <Medal className="w-4 h-4 text-gray-400" />}
                  </div>

                  <p className={`text-sm font-semibold truncate ${entry.isPlayer ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-800 dark:text-gray-100'}`}>
                    {entry.name}{entry.isPlayer ? '（我）' : ''}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {formatValue(entry.value, activeBoard, hasTrainingData)}
                  </p>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">完整排名</span>
            <span className="text-[11px] text-gray-400 dark:text-gray-500">当前维度：{tabConfig.label}</span>
          </div>

          <div className="divide-y divide-gray-100 dark:divide-gray-700/60 max-h-[460px] overflow-y-auto">
            {leaderboard.map((entry, idx) => (
              <motion.div
                key={`${entry.name}-${idx}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.015 }}
                className={`px-4 py-3 ${entry.isPlayer ? 'bg-indigo-50/70 dark:bg-indigo-900/15' : 'bg-transparent'}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 text-center text-xs font-bold ${entry.isPlayer ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400'}`}>
                    {idx + 1}
                  </div>

                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                    entry.isPlayer
                      ? 'bg-indigo-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                  }`}>
                    {entry.name.slice(0, 1)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${entry.isPlayer ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-800 dark:text-gray-100'}`}>
                      {entry.name}{entry.isPlayer ? '（我）' : ''}
                    </p>
                    <div className="mt-1.5 w-full h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${entry.isPlayer ? 'bg-indigo-500' : 'bg-gray-400 dark:bg-gray-500'}`}
                        style={{ width: `${Math.max(8, getBarWidth(entry.value))}%` }}
                      />
                    </div>
                  </div>

                  <div className="text-right">
                    <p className={`text-sm font-semibold ${entry.isPlayer ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-800 dark:text-gray-100'}`}>
                      {formatValue(entry.value, activeBoard, hasTrainingData)}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <p className="text-center text-[11px] text-gray-400 dark:text-gray-500">
          排行基于当前训练记录生成，持续训练会动态刷新位置。
        </p>
      </div>
    </div>
  );
}
